import { useState } from "react";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import type { DictationChosenSource } from "@thalamos/aspire-common";

import { Button } from "../Button/Button.js";

/**
 * Whether dictated text replaces what is already in the field or is added after
 * it. Only a question when the field is not empty.
 */
export type DictationInsertMode = "append" | "overwrite";

export type TortusResultChooserProps = {
  /** Plain-text rendering of the AI note, or null when Tortus returned none. */
  note: string | null;
  /** Verbatim speech-to-text, or null when Tortus returned none. */
  transcript: string | null;
  /**
   * Text already in the field this will land in. When it is non-empty the
   * insert-mode choice appears; otherwise there is nothing to decide.
   */
  existingText?: string;
  onPick: (choice: {
    chosen: string;
    chosenSource: DictationChosenSource;
    insertMode: DictationInsertMode;
  }) => void;
  onCancel: () => void;
};

/**
 * Offers the outputs of a finished dictation and writes the chosen one into the
 * field.
 *
 * The AI note leads and the raw transcript is collapsed beneath it: the note is
 * what all but a few users want, and a full transcript is long enough to bury the
 * note if both are open at once.
 *
 * Only these two outputs. The note's individual sections were offered as cards
 * too, but each is a slice of the full note already on offer, so they added length
 * without adding a choice. Sections are still carried on the completion event for
 * the form-level flow, which distributes them across fields rather than asking the
 * user to pick one.
 *
 * Laid out in a single column and scrolled internally, because this renders inside
 * the docked panel as well as a dialog.
 */
export const TortusResultChooser = ({
  note,
  transcript,
  existingText,
  onPick,
  onCancel,
}: TortusResultChooserProps) => {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  // Append matches what insertion has always done, and is the choice that cannot
  // destroy work the user has already typed.
  const [insertMode, setInsertMode] = useState<DictationInsertMode>("append");
  const fieldHasText = (existingText ?? "").trim().length > 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        flex: 1,
        p: 2,
        gap: 2,
        overflow: "auto",
      }}
    >
      <Box>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Choose how to fill the field
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Only the chosen text is written into the field; the rest is discarded.
        </Typography>
      </Box>

      {fieldHasText && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            This field already has text
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={insertMode}
            onChange={(_, next: DictationInsertMode | null) => {
              // A group with exclusive selection reports null when the active
              // button is pressed again; keep the current mode rather than
              // leaving no mode selected.
              if (next !== null) setInsertMode(next);
            }}
            aria-label="How to combine the dictated text with the existing text"
          >
            <ToggleButton value="append" data-testid="insertModeAppend">
              Add after it
            </ToggleButton>
            <ToggleButton value="overwrite" data-testid="insertModeOverwrite">
              Replace it
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <ResultPreviewCard
        title="Full AI note"
        subtitle="Structured note from Tortus's template"
        content={note}
        useLabel="Use AI note"
        onPick={(chosen) => {
          onPick({ chosen, chosenSource: "note", insertMode });
        }}
        testId="useFullAiNote"
      />

      <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
        <Box
          component="button"
          type="button"
          onClick={() => {
            setTranscriptOpen((wasOpen) => !wasOpen);
          }}
          aria-expanded={transcriptOpen}
          data-testid="toggleRawTranscript"
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            p: 2,
            font: "inherit",
            textAlign: "left",
            color: "inherit",
            bgcolor: "transparent",
            border: 0,
            cursor: "pointer",
          }}
        >
          <Box>
            <Typography variant="subtitle1">Raw transcript</Typography>
            <Typography variant="caption" color="text.secondary">
              Verbatim speech-to-text
            </Typography>
          </Box>
          <ExpandMore
            sx={{
              transform: transcriptOpen ? "rotate(180deg)" : "none",
              transition: "transform 150ms",
            }}
          />
        </Box>
        <Collapse in={transcriptOpen} unmountOnExit>
          <Box
            sx={{
              px: 2,
              pb: 2,
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <PreviewText content={transcript} />
            <Button
              primary
              label="Use raw transcript"
              disabled={!transcript}
              onClick={() => {
                if (transcript)
                  onPick({
                    chosen: transcript,
                    chosenSource: "transcript",
                    insertMode,
                  });
              }}
              testId="useRawTranscript"
            />
          </Box>
        </Collapse>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button outlined label="Cancel — discard all" onClick={onCancel} />
      </Box>
    </Box>
  );
};

const PreviewText = ({ content }: { content: string | null }) => (
  <Box
    sx={{
      maxHeight: 240,
      overflow: "auto",
      fontFamily: "monospace",
      fontSize: 12,
      whiteSpace: "pre-wrap",
      bgcolor: "grey.50",
      p: 1,
      borderRadius: 0.5,
      minHeight: 80,
    }}
  >
    {content ?? <em>Not returned by Tortus.</em>}
  </Box>
);

const ResultPreviewCard = ({
  title,
  subtitle,
  content,
  useLabel,
  onPick,
  testId,
}: {
  title: string;
  subtitle: string;
  content: string | null;
  useLabel: string;
  onPick: (text: string) => void;
  testId: string;
}) => (
  <Box
    sx={{
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      p: 2,
      display: "flex",
      flexDirection: "column",
      gap: 1,
      bgcolor: content ? "background.paper" : "action.disabledBackground",
    }}
  >
    <Box>
      <Typography variant="subtitle1">{title}</Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
    <PreviewText content={content} />
    <Button
      primary
      label={useLabel}
      disabled={!content}
      onClick={() => content && onPick(content)}
      testId={testId}
    />
  </Box>
);
